<template>
  <Teleport to="body">
    <Transition name="modal" @after-enter="focusDialog">
      <div v-if="isOpen" ref="backdrop" class="modal-backdrop" @click.self="emit('close')">
        <div
          ref="dialog"
          class="modal"
          :class="`is-${size}`"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          tabindex="-1"
        >
          <header class="modal-head">
            <slot name="leading"></slot>
            <div class="modal-heading">
              <h2 :id="titleId">{{ title }}</h2>
              <p v-if="subtitle" class="modal-subtitle">{{ subtitle }}</p>
            </div>
            <button type="button" class="modal-close" aria-label="Close" @click="emit('close')">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </header>
          <div class="modal-body">
            <slot></slot>
          </div>
          <footer v-if="$slots.footer" class="modal-foot">
            <slot name="footer"></slot>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'md'
  }
});

const emit = defineEmits(['close']);

const titleId = `modal-title-${Math.random().toString(36).slice(2, 9)}`;
const backdrop = ref(null);
const dialog = ref(null);
let returnFocusTo = null;

const focusDialog = () => {
  dialog.value?.focus({ preventScroll: true });
};

const handleKeydown = (event) => {
  if (event.key !== 'Escape' || !backdrop.value) return;
  const backdrops = document.querySelectorAll('.modal-backdrop');
  if (backdrops[backdrops.length - 1] === backdrop.value) {
    event.stopPropagation();
    emit('close');
  }
};

const setOpen = (open) => {
  if (open) {
    returnFocusTo = document.activeElement;
    document.addEventListener('keydown', handleKeydown);
    document.body.dataset.modals = String(Number(document.body.dataset.modals || 0) + 1);
    document.body.style.overflow = 'hidden';
  } else {
    document.removeEventListener('keydown', handleKeydown);
    const remaining = Math.max(Number(document.body.dataset.modals || 1) - 1, 0);
    document.body.dataset.modals = String(remaining);
    if (remaining === 0) document.body.style.overflow = '';
    if (returnFocusTo instanceof HTMLElement && document.contains(returnFocusTo)) {
      returnFocusTo.focus({ preventScroll: true });
    }
    returnFocusTo = null;
  }
};

watch(
  () => props.isOpen,
  (open, wasOpen) => {
    if (open !== !!wasOpen) setOpen(open);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (props.isOpen) setOpen(false);
});
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background-color: var(--scrim);
}

.modal {
  width: 100%;
  max-height: min(92vh, 92dvh);
  display: flex;
  flex-direction: column;
  --ink: var(--on-table);
  --ink-muted: var(--on-table-muted);
  --card-line: var(--table-line);
  --card-sunk: rgba(255, 255, 255, 0.05);
  --control: var(--sheet-raised);
  --strong: var(--accent);
  --on-strong: var(--on-accent);
  --red: var(--red-on-table);
  background-color: var(--sheet);
  color: var(--ink);
  border: 1px solid var(--panel-edge);
  border-bottom: none;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -12px 40px -12px rgba(0, 0, 0, 0.6);
  padding-bottom: env(safe-area-inset-bottom);
}

.modal:focus {
  outline: none;
}

.modal-head {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1.25rem 1.25rem 0.75rem;
}

.modal-heading {
  flex: 1;
  min-width: 0;
}

.modal-head h2 {
  font-size: var(--step-2);
  line-height: 1.1;
}

.modal-subtitle {
  margin-top: 0.25rem;
  color: var(--ink-muted);
  font-size: var(--step--1);
}

.modal-close {
  flex: none;
  width: 40px;
  height: 40px;
  margin: -0.375rem -0.5rem 0 0;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--ink-muted);
}

.modal-close:hover {
  background-color: var(--card-sunk);
  color: var(--ink);
}

.modal-close:focus-visible {
  outline-color: var(--strong);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem 1.25rem 1rem;
  overscroll-behavior: contain;
}

.modal-foot {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem 1.25rem;
  border-top: 1px solid var(--card-line);
}

.modal-foot :slotted(.status) {
  font-size: var(--step--1);
  color: var(--ink-muted);
  text-align: center;
}

.modal :deep(:focus-visible) {
  outline-color: var(--strong);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: translateY(32px);
}

@media (min-width: 640px) {
  .modal-backdrop {
    align-items: center;
    padding: 2rem;
  }

  .modal {
    max-width: 460px;
    border-radius: 14px;
    border-bottom: 1px solid var(--panel-edge);
    max-height: 86vh;
    padding-bottom: 0;
  }

  .modal.is-sm {
    max-width: 340px;
  }

  .modal-enter-from .modal,
  .modal-leave-to .modal {
    transform: translateY(12px) scale(0.98);
  }
}
</style>
